import { Component } from 'react';
import { connect, Dispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { RouterProps, withRouter } from 'utils/route';
import { ApplicationState } from 'reducers/types';
import { AuthActions } from 'reducers/Auth/actions';
import { AuthSelectors } from 'reducers/Auth/selectors';
import { UserActions } from 'reducers/User/actions';
import { UserSelectors } from 'reducers/User/selectors';
import { Box, Flex } from '@chakra-ui/react';
import { LoginCard, RegisterCard, SEOHelmet } from 'components';
import { UserModel } from 'models';
import { NavigationService } from 'services';
import { generateSEOLink, generateSEOTitle } from 'utils/seo';
import { AppConstants } from '../../constants';

enum AuthStateType {
  LOGIN = 'login',
  REGISTER = 'register',
}

interface AuthViewProps extends RouterProps {
  user: UserModel;
  loginLoading: boolean;
  registerLoading: boolean;
  forgotPasswordLoading: boolean;
  loginByEmail: (email: string, password: string) => any;
  registerUser: (email: string, username: string, password: string) => any;
  sendForgotPasswordEmail: (email: string) => any;
}

interface AuthViewState {
  flowType: AuthStateType;
}

class Auth extends Component<AuthViewProps, AuthViewState> {
  state: AuthViewState = {
    flowType:
      this.props.params.type === 'register'
        ? AuthStateType.REGISTER
        : AuthStateType.LOGIN,
  };

  componentDidUpdate(prevProps: AuthViewProps) {
    const { params, user, navigate } = this.props;
    const { flowType } = this.state;

    if (!prevProps.user.isAuthenticated() && user.isAuthenticated()) {
      if (flowType === AuthStateType.REGISTER) {
        navigate(NavigationService.getUserConfirmationPath());
      } else {
        if (user.isConfirmed()) {
          navigate(NavigationService.getHomePath());
        } else {
          navigate(NavigationService.getUserConfirmationPath());
        }
      }
    }

    if (this.hasRouteChanged(prevProps) && params.type) {
      this.setState({
        flowType: this.getFlowFromParamsType(params.type),
      });
    }
  }

  getFlowFromParamsType = (type: string): AuthStateType => {
    return type === 'register' ? AuthStateType.REGISTER : AuthStateType.LOGIN;
  };

  hasRouteChanged = (prevProps: AuthViewProps): boolean => {
    const { params } = this.props;
    return !!params.type && params.type !== prevProps.params.type;
  };

  onLoginSwitchClick = () => {
    this.setState({ flowType: AuthStateType.REGISTER });
  };

  onRegisterSwitchClick = () => {
    this.setState({ flowType: AuthStateType.LOGIN });
  };

  renderAuthContent() {
    const {
      loginByEmail,
      loginLoading,
      sendForgotPasswordEmail,
      forgotPasswordLoading,
      registerUser,
      registerLoading,
    } = this.props;

    if (this.state.flowType === AuthStateType.LOGIN) {
      return (
        <LoginCard
          onLoginClick={loginByEmail}
          onForgotPasswordClick={sendForgotPasswordEmail}
          submitLoading={loginLoading || forgotPasswordLoading}
          onSwitchToRegister={this.onLoginSwitchClick}
        />
      );
    }

    return (
      <RegisterCard
        onRegisterClick={registerUser}
        registerLoading={registerLoading}
        onSwitchToLogin={this.onRegisterSwitchClick}
      />
    );
  }

  render() {
    return (
      <>
        <SEOHelmet
          title={generateSEOTitle('Auth')}
          description={'Log In Or Register.'}
          link={generateSEOLink(NavigationService.getAuthLoginPath())}
        />
        <Box
          maxWidth={`${AppConstants.GRIDPAGE_WIDTH}px`}
          paddingTop={['80px', '100px', '100px']}
          justifySelf="center"
          minWidth={['100%', '600px']}
        >
          <Flex direction="column" alignItems="center" gap={10}>
            {this.renderAuthContent()}
          </Flex>
        </Box>
      </>
    );
  }
}

function mapStateToProps(state: ApplicationState) {
  return {
    user: AuthSelectors.getAuthenticatedUser(state),
    loginLoading: AuthSelectors.getUserLoginLoading(state),
    registerLoading: AuthSelectors.getUserRegisterLoading(state),
    forgotPasswordLoading: UserSelectors.getUserMailActionLoading(state),
  };
}

function mapDispatchToProps(dispatch: Dispatch<ApplicationState>) {
  return bindActionCreators(
    {
      loginByEmail: AuthActions.loginByEmail,
      registerUser: AuthActions.registerUser,
      sendForgotPasswordEmail: UserActions.sendForgotPasswordEmail,
    },
    dispatch
  );
}

export const AuthView = connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(Auth));
